/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';

import { FlexLayout, FlexItem } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';
import SelectHierarchyFilter from './SelectHierarchyFilter';
import FilterList from './FilterList';
import FilterPreferences from './FilterPreferences';
import FilterSearchInput from './FilterSearchInput';
import FilterLoadingSpinner from './FilterLoadingSpinner';

/*
This is a common filter component that show hierarchy filter either in tree shape or flat list.
*/
export default class HierarchyFilterPane extends Component<any, any> {
	// Define properties and types
	static propTypes = {
		onDoneSearch: PropTypes.func,
		loading: PropTypes.bool,
		// searchBox.HierarchyFilter
		showSearch: PropTypes.bool.isRequired,
		allowSearch: PropTypes.bool,
		onSearch: PropTypes.func.isRequired,
		showFilterPreferences: PropTypes.bool,
		isSearching: PropTypes.bool,
		// Tree props.
		getValue: PropTypes.func.isRequired,
		getNumberOfChildren: PropTypes.func.isRequired,
		onExpandCollapse: PropTypes.func.isRequired,
		onClick: PropTypes.func.isRequired,
		onClear: PropTypes.func,
		contentRenderer: PropTypes.func,
		limitChildren: PropTypes.number,
		onInvert: PropTypes.func.isRequired,
		selectedValuesCount: PropTypes.number,
		// List props.
		listOnClick: PropTypes.func,
		listGetValue: PropTypes.func,
		listGetNumberOfChildren: PropTypes.func,
		listContentRenderer: PropTypes.func,
		listOnInvert: PropTypes.func,
		showSubcaption: PropTypes.bool,
		readOnly: PropTypes.bool,
		invertDisabled: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		showFilterPreferences: true,
		limitChildren: 0,
		selectedValuesCount: 0,
		showSubcaption: true,
		loading: false,
		readOnly: false,
		allowSearch: true,
		invertDisabled: false,
		isSearching: false,
	};

	state = {
		searchText: '',
		limitChildren: this.props.limitChildren,
		showTree: true,
		inverted: false,
		listInverted: false,
	};

	_tree: SelectHierarchyFilter;

	_onSearchTextChange = result => {
		const searchText = result;
		const { onSearch } = this.props;

		if (searchText !== this.state.searchText) {
			onSearch && onSearch(searchText);
			let { showTree } = this.state;

			if (this.state.showTree && searchText !== '') {
				showTree = false;
			}
			this.setState({ searchText, showTree });
		}
	};

	// Preferences have changed
	_onPreferencesChanged = preferences => {
		const { onInvert, listOnInvert } = this.props;
		const inverted = !!preferences.inverted;

		if (this.state.showTree && inverted !== this.state.inverted) {
			this.setState({ inverted });
			onInvert && onInvert();
		}

		const listInverted = !!preferences.inverted;

		if (!this.state.showTree && listInverted !== this.state.listInverted) {
			this.setState({ listInverted });
			if (listOnInvert) {
				listOnInvert();
			} else if (onInvert) {
				onInvert();
			}
		}
	};

	_refTree = tree => {
		this._tree = tree;
	};

	_resetTree = () => {
		this._tree && this._tree.initSpareTree();
	};

	returnToTree = () => {
		const { onDoneSearch } = this.props;
		const searchText = '';
		const showTree = true;

		this.setState({ searchText, showTree });
		if (this.props.onDoneSearch) {
			onDoneSearch();
		}
	};

	_renderHierarchy = () => {
		const {
			showSearch,
			allowSearch,
			getValue,
			getNumberOfChildren,
			onExpandCollapse,
			onClick,
			limitChildren,
			contentRenderer,
			readOnly,
			listOnClick,
			listGetValue,
			listGetNumberOfChildren,
			listContentRenderer,
			isSearching,
		} = this.props;

		if (limitChildren !== this.state.limitChildren) {
			this.setState({ limitChildren }, this._resetTree);
		}
		const listGetNumberOfChildrenFn = listGetNumberOfChildren || getNumberOfChildren;
		const totalValues = this.state.showTree ? 0 : listGetNumberOfChildrenFn();

		const borderStyle = {
			'border-color': '#F2F2F2',
			'border-width': '1px',
			'border-style': 'solid',
		};

		return (
			<FlexLayout
				justifyContent="space-between"
				fullHeight
				direction="column"
				style={borderStyle}
			>
				{allowSearch === true && showSearch === true && (
					<FlexItem gutter={0}>
						<FilterSearchInput
							fullWidth
							placeholder={stringResource.get('find')}
							value={this.state.searchText}
							onChange={this._onSearchTextChange}
							readOnly={readOnly}
						/>
					</FlexItem>
				)}
				<FlexItem grow>
					{this.state.showTree ? (
						<SelectHierarchyFilter
							getValue={getValue}
							getNumberOfChildren={getNumberOfChildren}
							onExpandCollapse={onExpandCollapse}
							onClick={onClick}
							ref={this._refTree}
							limitChildrenCnt={limitChildren}
							contentRenderer={contentRenderer}
							selectionMode="single"
							readOnly={readOnly}
							ariaMultiselectable
						/>
					) : (
						<FilterList
							totalValues={totalValues}
							onClick={listOnClick || onClick}
							getValue={listGetValue || getValue}
							listGetNumberOfChildren={listGetNumberOfChildren || getNumberOfChildren}
							contentRenderer={listContentRenderer || contentRenderer}
							showPath={this.props.showSubcaption}
							readOnly={readOnly}
							isSearching={isSearching}
						/>
					)}
				</FlexItem>
			</FlexLayout>
		);
	};

	_renderHierarchyFilterPane = () => {
		const { showFilterPreferences, limitChildren, selectedValuesCount, readOnly } = this.props;

		if (limitChildren !== this.state.limitChildren) {
			this.setState({ limitChildren }, this._resetTree);
		}

		return (
			<FlexLayout justifyContent="space-between" fullHeight direction="column">
				<FlexItem grow>{this._renderHierarchy()}</FlexItem>
				{showFilterPreferences && (
					<FlexItem gutter={[2, 0, 0, 0]}>
						<FilterPreferences
							showInvertSelected={false}
							inverted={
								this.state.showTree ? this.state.inverted : this.state.listInverted
							}
							includeNulls={false}
							onChange={this._onPreferencesChanged}
							onClear={this.props.onClear}
							showIncludeNull={false}
							selectedValuesCount={selectedValuesCount}
							showReturnToTree={!this.state.showTree}
							returnToTree={this.returnToTree}
							readOnly={readOnly}
							invertDisabled={this.props.invertDisabled}
						/>
					</FlexItem>
				)}
			</FlexLayout>
		);
	};

	render() {
		const { loading } = this.props;

		if (loading) {
			return <FilterLoadingSpinner />;
		}

		return this._renderHierarchyFilterPane();
	}
}
